import React, { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, Search, Link as LinkIcon, CheckCircle, Plus, ExternalLink, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ApiService from '../../services/ApiService';

function ReviewCenter({ onCreateDraft, onEdit }) {
  const [loading, setLoading] = useState(true);
  const [isTaskRunning, setIsTaskRunning] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    contentOpportunities: [], // from failed_searches
    needsReview: [], // from needs_review
    linkOpportunities: [] // placeholder or future use
  });

  useEffect(() => {
    loadData();
  }, []);

  const handleRunTask = async (taskFunction, successMessage) => {
    setIsTaskRunning(true);
    try {
      await taskFunction();
      toast.success(successMessage);
      loadData(); // Refresh data after task runs
    } catch (error) {
      console.error("Task failed:", error);
      toast.error("An error occurred while running the task.");
    } finally {
      setIsTaskRunning(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Check if organization context is available
      if (!window.Clerk?.organization?.id) {
        setError("No organization selected. Please select an organization to view review data.");
        setLoading(false);
        return;
      }

      const response = await ApiService.getReviewDashboardData();
      setData({
        contentOpportunities: response.failed_searches || [],
        needsReview: response.needs_review || [],
        linkOpportunities: [] // Not available in current API response
      });
    } catch (error) {
      console.error("Failed to load review center data", error);
      const errorMsg = error.response?.data?.message || error.message || "Unknown error occurred";
      setError(`Failed to load review data: ${errorMsg}`);
      // Don't show toast, just set error state
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (faq) => {
      try {
        await ApiService.updateFAQ({
            faq_id: faq.faq_id,
            needs_review: false,
            status: 'public'
        });
        toast.success("FAQ Approved");
        loadData();
      } catch (e) {
          toast.error("Failed to approve");
      }
  }

  if (loading) {
      return <div className="flex justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-black"/></div>
  }

  if (error) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-black flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-black font-semibold mb-1">Unable to Load Review Data</h3>
            <p className="text-gray-700 text-sm mb-4">{error}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 text-sm font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Analysis Tools */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Analysis Tools</h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => handleRunTask(() => ApiService.findContentOpportunities(), "Started finding content opportunities.")}
            disabled={isTaskRunning}
            className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
          >
            {isTaskRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Find Content Opportunities
          </button>
          <button
            onClick={() => handleRunTask(() => ApiService.flagFAQsForReview(), "Started flagging FAQs for review.")}
            disabled={isTaskRunning}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
          >
            {isTaskRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4 text-black" />}
            Flag FAQs for Review
          </button>
          <button
            onClick={() => handleRunTask(() => ApiService.findLinkOpportunities(), "Started finding link opportunities.")}
            disabled={isTaskRunning}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
          >
            {isTaskRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4 text-black" />}
            Find Link Opportunities
          </button>
        </div>
      </div>

      {/* 1. New Content Opportunities */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-200 rounded-lg">
                    <Search className="h-5 w-5 text-black" />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-gray-900">New Content Opportunities</h3>
                    <p className="text-sm text-gray-600">Questions your users are asking that aren't answered yet.</p>
                </div>
            </div>
            <span className="bg-gray-200 text-black px-3 py-1 rounded-full text-sm font-medium">
                {data.contentOpportunities.length} Suggestions
            </span>
        </div>
        <div className="divide-y divide-gray-100">
            {data.contentOpportunities.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No content opportunities found. Try running the analysis tool.</div>
            ) : (
                data.contentOpportunities.map((item, idx) => (
                    <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                        <div>
                            <p className="font-medium text-gray-900">{item.query}</p>
                            <p className="text-sm text-gray-500">{item.count} searches</p>
                        </div>
                        <button 
                            onClick={() => onCreateDraft(item.query)}
                            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition-colors"
                        >
                            <Plus className="h-4 w-4" />
                            Create Draft
                        </button>
                    </div>
                ))
            )}
        </div>
      </section>

      {/* 2. Content Needing Review */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-200 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-black" />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-gray-900">Content Needing Review</h3>
                    <p className="text-sm text-gray-600">FAQs flagged for update or quality check.</p>
                </div>
            </div>
            <span className="bg-gray-200 text-black px-3 py-1 rounded-full text-sm font-medium">
                {data.needsReview.length} Items
            </span>
        </div>
        <div className="divide-y divide-gray-100">
            {data.needsReview.length === 0 ? (
                <div className="p-8 text-center text-gray-500">All caught up! No content needs review.</div>
            ) : (
                data.needsReview.map((faq) => (
                    <div key={faq.faq_id} className="p-4 flex items-start justify-between hover:bg-gray-50 transition-colors">
                        <div className="flex-1 mr-4">
                            <p className="font-medium text-gray-900">{faq.question}</p>
                            <p className="text-sm text-gray-500 line-clamp-2 mt-1">{faq.answer}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button 
                                onClick={() => onEdit(faq)}
                                className="px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                            >
                                Edit
                            </button>
                            <button 
                                onClick={() => handleApprove(faq)}
                                className="flex items-center gap-2 px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
                            >
                                <CheckCircle className="h-4 w-4" />
                                Approve
                            </button>
                        </div>
                    </div>
                ))
            )}
        </div>
      </section>

      {/* 3. Internal Linking Suggestions */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-200 rounded-lg">
                    <LinkIcon className="h-5 w-5 text-black" />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-gray-900">Internal Linking Suggestions</h3>
                    <p className="text-sm text-gray-600">Opportunities to link FAQs to other pages.</p>
                </div>
            </div>
            <span className="bg-gray-200 text-black px-3 py-1 rounded-full text-sm font-medium">
                {data.linkOpportunities.length} Suggestions
            </span>
        </div>
        <div className="divide-y divide-gray-100">
             <div className="p-8 text-center text-gray-500">
                No linking suggestions available at this time. Run the "Find Link Opportunities" tool to generate suggestions.
             </div>
        </div>
      </section>
    </div>
  );
}

export default ReviewCenter;
